import {
  CareJourneyPreReviewData,
  ConfirmPreReviewRequest,
  ReviewDataEditRequest,
} from '@/api/dto/care-journey';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import Genders, {getGenderAbbreviation} from '@manual_encounters/constants/Genders';
import DisplayHelpers from '@/helpers/display_helpers';

/** Reviewer-editable working set for the pre-review wizard (seeded from the step context). */
export interface PreReviewForm {
  firstName: string;
  middleName: string;
  lastName: string;
  dob: string;
  gender: string;
  clientId: string;
  addressLine1: string;
  addressLine2: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  phoneHome: string;
  phoneMobile: string;
  reasonForReferral: string;
  // Picker-selected PCP (CARECO-5762); optional per Decision #16. pcpPractitionerId is the Registry
  // key (thoth practitioners.id UUID); pcpNpi is nullable and only a snapshot, so it cannot be the key.
  pcpPractitionerId: string;
  pcpNpi: string;
  pcpName: string;
  // CARECO-6351: structured PCP name, split from the picker displayName (splitProviderName), so the
  // backend can forward a first/last-structured `pcp` to OE for Intake prefill. Blank when the
  // displayName can't be split confidently (the PCP is then omitted from OE — no wrong guess).
  pcpFirstName: string;
  pcpLastName: string;
  // CARECO-6351 (QA 1332363): PCP phone from the Registry pick (selection.phone), carried through
  // so the backend forwards it to OE and the Intake PCP phone prefills. Blank when the pick has none.
  pcpPhone: string;
  // CARECO-6363: PCP specialty/title and email from the Registry pick, for the rich PCP card.
  pcpTitle: string;
  pcpEmail: string;
  practiceId: string;
  practiceName: string;
  // CARECO-6369 / ORANGE-28789: enriched Referring Practice fields from the PracticeSelect payload
  // (org line + contact column on the Create-step card). Blank until Orange ships the fields; not
  // persisted to the backend yet, so a reopened draft shows name-only until the practice is
  // re-picked (same picker-deferred behavior as PCP, CARECO-5762).
  practiceOrganizationName: string;
  practicePhone: string;
  practiceEmail: string;
  practiceWebsite: string;
  practiceAddress1: string;
  practiceAddress2: string;
  practiceCity: string;
  practiceState: string;
  practiceZip: string;
  // CARECO-6285: set by the Referring Provider card's remove (×). Sent as removeReferringProvider so
  // the backend clears the ingested referring_provider blob from the record (hard removal, no undo).
  referringProviderRemoved: boolean;
}

/**
 * Keys of {@link PreReviewForm} whose value is a string. The generic text/select/validation helpers
 * only operate on these; non-string fields (e.g. referringProviderRemoved) are handled explicitly.
 */
export type PreReviewStringField = {
  [K in keyof PreReviewForm]: PreReviewForm[K] extends string ? K : never
}[keyof PreReviewForm];

const str = (v: string | undefined | null): string => v ?? '';

// CARECO-6351 — PCP name splitting (stopgap until thoth/ProviderSelect returns structured names).
// The Registry picker hands careco-ui only a single `displayName`, but OE's direct `pcp` contract
// requires first AND last (all-or-nothing). splitProviderName derives them confidently or returns
// blanks, in which case the PCP is omitted downstream and the Intake PCP stays empty — never
// prefilled with a wrong guess (no regression vs today).
const PROVIDER_CREDENTIAL = new RegExp(
  '^(' + [
    'MD', 'DO', 'DMD', 'DDS', 'NP', 'APRN', 'FNP', 'PMHNP', 'PA', 'PA-C', 'RN', 'LPN', 'LVN',
    'LCSW', 'LMFT', 'LPC', 'LMHC', 'PHD', 'PSYD', 'PT', 'OT', 'DPT', 'DPM', 'OD', 'PHARMD',
    'MBBS', 'MSN', 'MSW', 'MPH', 'ESQ', 'JR', 'SR', 'II', 'III', 'IV',
  ].join('|') + ')$',
  'i',
);
const PROVIDER_HONORIFIC = /^(DR|MR|MRS|MS|MISS|PROF)$/i;

const stripPunct = (token: string): string => token.replace(/[.,]/g, '');
// Board-certification suffixes attach to a credential root with a hyphen (e.g. FNP-BC, PMHNP-BC,
// PA-C). Treat a token as a credential when the whole token OR its root (before the first hyphen)
// is a known credential, so unrecognized cert variants are stripped like "MD" rather than mistaken
// for a name. A hyphenated real name (e.g. "Jo-An") has a non-credential root and is preserved.
const isCredentialToken = (token: string): boolean => {
  const cleaned = stripPunct(token);
  return PROVIDER_CREDENTIAL.test(cleaned) || PROVIDER_CREDENTIAL.test(cleaned.split('-')[0]);
};

// Space-separated "First [MI] Last" (and compound last names). Drops a leading honorific and any
// trailing credential tokens. Requires >= 2 surviving tokens, else returns blank (not confident).
function splitSpaceSeparated(name: string): {first: string; last: string} {
  let tokens = name.split(' ').filter(Boolean);
  if (tokens.length && PROVIDER_HONORIFIC.test(stripPunct(tokens[0]))) {
    tokens = tokens.slice(1);
  }
  while (tokens.length && isCredentialToken(tokens[tokens.length - 1])) {
    tokens = tokens.slice(0, -1);
  }
  if (tokens.length < 2) {
    return {first: '', last: ''};
  }
  const first = tokens[0];
  let rest = tokens.slice(1);
  // Drop a single isolated middle initial (one letter, optional dot) between first and last so
  // "Jane A Smith" -> last "Smith". Guarded by a remaining surname token, and only single-letter
  // tokens qualify, so compound surnames like "Van Berg" stay intact.
  if (rest.length >= 2 && /^[A-Za-z]\.?$/.test(rest[0])) {
    rest = rest.slice(1);
  }
  return {first, last: rest.join(' ')};
}

/**
 * Splits a Registry provider displayName into {first, last}. Handles "First Last", "First M Last",
 * compound last names ("Jane Van Berg"), "Last, First", leading honorifics (Dr./Ms.), and trailing
 * credentials (", MD" / " PA-C"). Returns blank strings when it cannot split reliably (single
 * token, empty, honorific/credential-only) so the caller omits the PCP rather than guessing.
 */
export function splitProviderName(displayName?: string | null): {first: string; last: string} {
  const raw = (displayName ?? '').trim().replace(/\s+/g, ' ');
  if (!raw) {
    return {first: '', last: ''};
  }
  if (raw.includes(',')) {
    const nameSegments = raw.split(',').map(s => s.trim()).filter(Boolean)
      .filter(segment => !isCredentialToken(segment));
    if (nameSegments.length >= 2) {
      // "Last, First [MI]" — surname first, given name(s) second. Drop a leading honorific from the
      // surname segment (e.g. "Dr. Smith, Jane" -> last "Smith") while keeping compound surnames.
      const lastTokens = nameSegments[0].split(' ').filter(Boolean);
      if (lastTokens.length && PROVIDER_HONORIFIC.test(stripPunct(lastTokens[0]))) {
        lastTokens.shift();
      }
      const last = lastTokens.join(' ');
      const first = nameSegments[1].split(' ')
        .filter(t => t && !isCredentialToken(t) && !PROVIDER_HONORIFIC.test(stripPunct(t)))[0] ?? '';
      return first && last ? {first, last} : {first: '', last: ''};
    }
    // Only one name segment survived (e.g. "Jane Smith, MD") — split it on spaces.
    return splitSpaceSeparated(nameSegments[0] ?? '');
  }
  return splitSpaceSeparated(raw);
}

// Gender dropdown options for pre-review, keyed by the single-letter CODE the backend stores/forwards
// (so form.gender stays the code end-to-end) with full-word labels for display. Derived from Genders
// via getGenderAbbreviation so the code↔word pairing can't drift from the shared constant. Unknown is
// intentionally excluded: an Unknown/unmapped referral has no option, renders empty, and must be
// re-selected to pass gender validation.
export const PRE_REVIEW_GENDER_OPTIONS: Value[] =
  [Genders.MALE, Genders.FEMALE, Genders.OTHER, Genders.NON_BINARY].map(word => ({
    value: getGenderAbbreviation(word),
    label: word,
  }));

const VALID_GENDER_CODES = new Set(PRE_REVIEW_GENDER_OPTIONS.map(option => option.value));

/** Seeds the working form from the backend step-context prefill. */
export function toInitialForm(data?: CareJourneyPreReviewData): PreReviewForm {
  const patient = data?.patient;
  const journey = data?.journey;
  const review = data?.review;
  return {
    firstName: str(patient?.firstName),
    middleName: str(patient?.middleName),
    lastName: str(patient?.lastName),
    dob: str(patient?.dob),
    gender: str(patient?.gender),
    clientId: str(patient?.patientCustomerProvidedId),
    addressLine1: str(patient?.addressLine1),
    addressLine2: str(patient?.addressLine2),
    addressCity: str(patient?.addressCity),
    addressState: str(patient?.addressState),
    addressZip: str(patient?.addressZip),
    // Normalize at seed so an un-edited field submits a clean number, not a raw "+1..." (CARECO-6168).
    phoneHome: DisplayHelpers.normalizeUsPhoneDigits(patient?.phoneHome),
    phoneMobile: DisplayHelpers.normalizeUsPhoneDigits(patient?.phoneMobile),
    reasonForReferral: str(journey?.reasonForReferral),
    // Rehydrate the picker selections persisted on referral_review_data so a saved draft/confirmed
    // review shows what was picked (CARECO-5762); the cards render name-only until re-picked.
    pcpPractitionerId: str(review?.pcpPractitionerId),
    pcpNpi: str(review?.pcpNpi),
    pcpName: str(review?.pcpName),
    pcpFirstName: str(review?.pcpFirstName),
    pcpLastName: str(review?.pcpLastName),
    pcpPhone: str(review?.pcpPhone),
    pcpTitle: str(review?.pcpTitle),
    pcpEmail: str(review?.pcpEmail),
    practiceId: str(review?.practiceId),
    practiceName: str(review?.practiceName),
    // CARECO-6408: rehydrate the enriched practice snapshot persisted on referral_review_data so the
    // Create Care Journey practice card reopens with org/contact/address (mirrors the pcp* fields).
    practiceOrganizationName: str(review?.practiceOrganizationName),
    practicePhone: str(review?.practicePhone),
    practiceEmail: str(review?.practiceEmail),
    practiceWebsite: str(review?.practiceWebsite),
    practiceAddress1: str(review?.practiceAddress1),
    practiceAddress2: str(review?.practiceAddress2),
    practiceCity: str(review?.practiceCity),
    practiceState: str(review?.practiceState),
    practiceZip: str(review?.practiceZip),
    // Removal is a per-session reviewer action, not persisted prefill; always starts false. Once
    // confirmed/saved the backend drops the blob, so a reopened review simply has no referring
    // provider to show (CARECO-6285).
    referringProviderRemoved: false,
  };
}

/** Required client-profile fields (per Figma asterisks). */
export const CLIENT_PROFILE_REQUIRED: PreReviewStringField[] = [
  'firstName',
  'lastName',
  'dob',
  'gender',
  'addressLine1',
  'addressCity',
  'addressState',
  'addressZip',
  'phoneHome',
];

/** A complete US phone is exactly 10 digits; the phone input stores raw digits (CARECO-6315). */
const isCompletePhone = (value: string): boolean => value.replace(/\D/g, '').length === 10;

/** Shown when a phone field holds a non-blank but incomplete number (fewer than 10 digits). */
export const INCOMPLETE_PHONE_MESSAGE = 'Enter a valid 10-digit phone number';

/** A valid US ZIP is 5 digits, optionally followed by a 4-digit ZIP+4 (e.g. 19804 or 19804-1234). */
const isValidZip = (value: string): boolean => /^\d{5}(-\d{4})?$/.test(value.trim());

/** Shown when the ZIP field holds a non-blank value that is not a valid 5- or 9-digit US ZIP. */
export const INVALID_ZIP_MESSAGE = 'Enter a valid ZIP code';

/** Field-level errors for the Review Client Profile step (empty when valid). */
export function clientProfileErrors(form: PreReviewForm): Partial<Record<PreReviewStringField, string>> {
  const errors: Partial<Record<PreReviewStringField, string>> = {};
  for (const field of CLIENT_PROFILE_REQUIRED) {
    if (!form[field].trim()) {
      errors[field] = 'This field is required';
    }
  }
  // Gender has no Unknown option, so an Unknown/unmapped code (non-blank but unselectable) must
  // still block: it reads as empty in the dropdown and has to be re-selected (CARECO-6151/6080).
  if (!errors.gender && !VALID_GENDER_CODES.has(form.gender)) {
    errors.gender = 'This field is required';
  }
  // phoneHome is required AND must be a complete 10-digit number — presence alone is not enough, so a
  // partial number can't pass the Confirm gate and reach toConfirmRequest (CARECO-6315). The required
  // loop above already flags a blank value, so only guard the non-blank incomplete case here.
  if (!errors.phoneHome && form.phoneHome.trim() && !isCompletePhone(form.phoneHome)) {
    errors.phoneHome = INCOMPLETE_PHONE_MESSAGE;
  }
  // phoneMobile is optional (not in CLIENT_PROFILE_REQUIRED), but a non-blank partial entry is still
  // invalid and would otherwise submit the same incomplete value (CARECO-6315).
  if (form.phoneMobile.trim() && !isCompletePhone(form.phoneMobile)) {
    errors.phoneMobile = INCOMPLETE_PHONE_MESSAGE;
  }
  // addressZip is required AND must be a valid US ZIP — presence alone let free text like "d" pass
  // (CARECO-6315). The required loop above already flags a blank value, so only guard the non-blank
  // malformed case here.
  if (!errors.addressZip && form.addressZip.trim() && !isValidZip(form.addressZip)) {
    errors.addressZip = INVALID_ZIP_MESSAGE;
  }
  return errors;
}

export function isClientProfileValid(form: PreReviewForm): boolean {
  return Object.keys(clientProfileErrors(form)).length === 0;
}

/**
 * Field-level errors for the Review Referral step (empty when valid). Only reason for referral is
 * enforced here; referring practice is picker-deferred (CARECO-5762) and gated at Confirm instead.
 */
export function reviewReferralErrors(form: PreReviewForm): Partial<Record<PreReviewStringField, string>> {
  const errors: Partial<Record<PreReviewStringField, string>> = {};
  if (!form.reasonForReferral.trim()) {
    errors.reasonForReferral = 'This field is required';
  }
  return errors;
}

/**
 * Whether the referral has a referring practice to display/confirm. The referring practice is
 * sourced from the referral (not the picker); its presence is part of the confirm gate.
 */
export function hasReferringPractice(data?: CareJourneyPreReviewData): boolean {
  return Boolean(data?.journey?.referralPractice?.name?.trim());
}

/** A single unmet confirm-gate condition, with a user-facing label for the warning banner. */
export interface UnmetConfirmCondition {
  key: 'clientProfile' | 'reasonForReferral' | 'referringPractice' | 'matchedPatient' | 'dataType';
  label: string;
}

/**
 * Confirm gate (Q9 / Decision #16): patient matched to Pings, dataType present (resolves the OE
 * journey), a practice selected from the Registry (CARECO-5762), reason for referral present, and a
 * valid client profile. The individual PCP provider and the broader clinical payload are not
 * required to Confirm.
 *
 * Returns the specific conditions still unmet (empty when the gate is satisfied) so the UI can tell
 * the navigator exactly what is missing. User-actionable items (client profile, referring practice,
 * reason for referral) are worded as actions; system/data conditions (matched patient, data type)
 * are worded as data states since the navigator does not fill them in on this screen.
 */
export function getUnmetConfirmConditions(
  form: PreReviewForm,
  data?: CareJourneyPreReviewData,
): UnmetConfirmCondition[] {
  const journey = data?.journey;
  const unmet: UnmetConfirmCondition[] = [];
  if (!isClientProfileValid(form)) {
    unmet.push({key: 'clientProfile', label: 'Complete the required client profile fields'});
  }
  if (!form.practiceId.trim()) {
    unmet.push({key: 'referringPractice', label: 'Select a practice from the Network Registry'});
  }
  if (!form.reasonForReferral.trim()) {
    unmet.push({key: 'reasonForReferral', label: 'Add a reason for referral'});
  }
  if (!journey?.pingsPatientId?.trim()) {
    unmet.push({key: 'matchedPatient', label: 'Patient is not yet matched in Pings'});
  }
  if (!journey?.dataType?.trim()) {
    unmet.push({key: 'dataType', label: 'Referral data type is missing'});
  }
  return unmet;
}

export function isConfirmReady(form: PreReviewForm, data?: CareJourneyPreReviewData): boolean {
  return getUnmetConfirmConditions(form, data).length === 0;
}

/** Maps the working form to the Confirm endpoint request. Blank optional fields become undefined. */
export function toConfirmRequest(form: PreReviewForm, taskId: string): ConfirmPreReviewRequest {
  const opt = (v: string): string | undefined => (v.trim() ? v : undefined);
  return {
    taskId,
    firstName: opt(form.firstName),
    middleName: opt(form.middleName),
    lastName: opt(form.lastName),
    dob: opt(form.dob),
    gender: opt(form.gender),
    addressLine1: opt(form.addressLine1),
    addressLine2: opt(form.addressLine2),
    addressCity: opt(form.addressCity),
    addressState: opt(form.addressState),
    addressZip: opt(form.addressZip),
    phoneHome: opt(form.phoneHome),
    phoneMobile: opt(form.phoneMobile),
    reasonForReferral: opt(form.reasonForReferral),
    pcpPractitionerId: opt(form.pcpPractitionerId),
    pcpNpi: opt(form.pcpNpi),
    pcpName: opt(form.pcpName),
    pcpFirstName: opt(form.pcpFirstName),
    pcpLastName: opt(form.pcpLastName),
    pcpPhone: opt(form.pcpPhone),
    pcpTitle: opt(form.pcpTitle),
    pcpEmail: opt(form.pcpEmail),
    practiceId: opt(form.practiceId),
    practiceName: opt(form.practiceName),
    practiceOrganizationName: opt(form.practiceOrganizationName),
    practicePhone: opt(form.practicePhone),
    practiceEmail: opt(form.practiceEmail),
    practiceWebsite: opt(form.practiceWebsite),
    practiceAddress1: opt(form.practiceAddress1),
    practiceAddress2: opt(form.practiceAddress2),
    practiceCity: opt(form.practiceCity),
    practiceState: opt(form.practiceState),
    practiceZip: opt(form.practiceZip),
    removeReferringProvider: form.referringProviderRemoved,
  };
}

/**
 * Maps the working form to the draft-save endpoint request (CARECO-5760) — the same editable
 * working set as {@link toConfirmRequest} without a taskId (saving a draft does not complete the
 * review task). Blank optional fields become undefined.
 */
export function toReviewDataEditRequest(form: PreReviewForm): ReviewDataEditRequest {
  const opt = (v: string): string | undefined => (v.trim() ? v : undefined);
  return {
    firstName: opt(form.firstName),
    middleName: opt(form.middleName),
    lastName: opt(form.lastName),
    dob: opt(form.dob),
    gender: opt(form.gender),
    addressLine1: opt(form.addressLine1),
    addressLine2: opt(form.addressLine2),
    addressCity: opt(form.addressCity),
    addressState: opt(form.addressState),
    addressZip: opt(form.addressZip),
    phoneHome: opt(form.phoneHome),
    phoneMobile: opt(form.phoneMobile),
    reasonForReferral: opt(form.reasonForReferral),
    pcpPractitionerId: opt(form.pcpPractitionerId),
    pcpNpi: opt(form.pcpNpi),
    pcpName: opt(form.pcpName),
    pcpFirstName: opt(form.pcpFirstName),
    pcpLastName: opt(form.pcpLastName),
    pcpPhone: opt(form.pcpPhone),
    pcpTitle: opt(form.pcpTitle),
    pcpEmail: opt(form.pcpEmail),
    practiceId: opt(form.practiceId),
    practiceName: opt(form.practiceName),
    practiceOrganizationName: opt(form.practiceOrganizationName),
    practicePhone: opt(form.practicePhone),
    practiceEmail: opt(form.practiceEmail),
    practiceWebsite: opt(form.practiceWebsite),
    practiceAddress1: opt(form.practiceAddress1),
    practiceAddress2: opt(form.practiceAddress2),
    practiceCity: opt(form.practiceCity),
    practiceState: opt(form.practiceState),
    practiceZip: opt(form.practiceZip),
    removeReferringProvider: form.referringProviderRemoved,
  };
}
